﻿/**
 * SHX Dynamic grid — reveal-on-view, theme-editor tile highlighting, and a
 * lightweight parallax fallback for browsers without CSS scroll-driven
 * animations (the primary path is pure CSS in the section's inline style).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ShxDynamicGrid extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    const cells = Array.from(this.querySelectorAll('.shx-dgrid__cell'));

    // The square-cell size and the explicit row count are measured here:
    // - CSS cannot derive ROW heights from the grid's WIDTH (a 100%-based
    //   custom property means "of the height" in row context and collapses),
    //   so the cell size is computed from clientWidth and set in px.
    // - The section can't read theme-block settings (section.blocks exposes
    //   blocks without settings), so the canvas depth comes from the cells'
    //   computed grid placement.
    // Re-measured on resize, breakpoint change, and editor re-renders.
    const grid = this.querySelector('[data-shx-grid]');
    const syncGrid = () => {
      if (!grid || grid.clientWidth === 0) return;
      const gs = getComputedStyle(grid);
      const cols = parseInt(gs.getPropertyValue('--shx-dg-cols'), 10) || 8;
      const gap = parseFloat(gs.getPropertyValue('--shx-dg-gap')) || 12;
      const cell = (grid.clientWidth - (cols - 1) * gap) / cols;
      grid.style.setProperty('--shx-dg-cell', cell.toFixed(2) + 'px');
      let max = 1;
      grid.querySelectorAll('.shx-dgrid__cell').forEach((c) => {
        const cs = getComputedStyle(c);
        if (cs.display === 'none') return;
        const row = parseInt(cs.gridRowStart, 10);
        if (!Number.isNaN(row) && row > max) max = row;
      });
      grid.style.setProperty('--shx-dg-rows', max);
    };
    syncGrid();
    this.gridResizeObserver = new ResizeObserver(() => requestAnimationFrame(syncGrid));
    if (grid) this.gridResizeObserver.observe(grid);
    window
      .matchMedia('(min-width: 990px)')
      .addEventListener('change', () => requestAnimationFrame(syncGrid), { signal });
    this.rowObserver = new MutationObserver(() => requestAnimationFrame(syncGrid));
    if (grid) this.rowObserver.observe(grid, { childList: true });

    // Reveal on first view — the stagger comes from CSS transition-delay
    // (each cell carries --shx-ri, its index, via the section's inline style).
    if (REDUCED_MOTION.matches) {
      cells.forEach((c) => c.classList.add('is-revealed'));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
      cells.forEach((c) => io.observe(c));
      this.revealObserver = io;
    }

    // Theme editor: show the grid squares while the section is selected, and
    // highlight the tile whose block is selected.
    if (window.Shopify && Shopify.designMode) {
      const sectionEl = this.closest('.shopify-section');
      sectionEl?.addEventListener('shopify:section:select', () => this.classList.add('editor-selected'), { signal });
      sectionEl?.addEventListener('shopify:section:deselect', () => this.classList.remove('editor-selected'), { signal });
      this.addEventListener('shopify:block:select', (e) => { this.classList.add('editor-selected'); e.target.classList.add('editor-selected'); }, { signal });
      this.addEventListener('shopify:block:deselect', (e) => e.target.classList.remove('editor-selected'), { signal });
    }

    // Parallax fallback: only when the browser lacks ViewTimeline support.
    if (!('ViewTimeline' in window) && !REDUCED_MOTION.matches) {
      this.setupFallbackParallax(cells, signal);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.revealObserver?.disconnect();
    this.nearObserver?.disconnect();
    this.rowObserver?.disconnect();
    this.gridResizeObserver?.disconnect();
  }

  setupFallbackParallax(cells, signal) {
    const moving = cells.filter((c) => c.hasAttribute('data-px'));
    if (moving.length === 0) return;

    let active = false;
    let ticking = false;

    const apply = () => {
      ticking = false;
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      // Progress of the section through the viewport: -1 (entering) → 1 (leaving)
      const t = Math.max(-1, Math.min(1, 1 - (2 * (rect.top + rect.height / 2)) / (vh + rect.height) + 0));
      const unit = Math.min(window.innerWidth, vh) / 100;
      const scale = window.innerWidth < 750 ? 2.5 : 5;
      /* SHX §3.20: the per-cell speed used to be read with getComputedStyle
         INSIDE this write loop, so every cell forced a fresh style recalc on
         every scroll frame (read-write-read-write thrash). The value only
         changes when the merchant edits the section, so it is resolved once
         and cached on the element; the loop below now writes only. */
      moving.forEach((cell) => {
        if (cell.__shxSpeed === undefined) {
          cell.__shxSpeed = parseFloat(getComputedStyle(cell).getPropertyValue('--shx-pxs')) || 0;
          cell.__shxAxis = cell.getAttribute('data-px') === 'h' ? 'X' : 'Y';
        }
        const speed = cell.__shxSpeed;
        if (!speed) return;
        const offset = -t * speed * scale * unit;
        cell.style.transform = `translate${cell.__shxAxis}(${offset.toFixed(1)}px)`;
      });
    };

    const onScroll = () => {
      if (!active || ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    // Attach the scroll listener only while the section is near the viewport.
    this.nearObserver = new IntersectionObserver(
      (entries) => {
        active = entries[0].isIntersecting;
        if (active) apply();
      },
      { rootMargin: '50% 0px 50% 0px' }
    );
    this.nearObserver.observe(this);
    window.addEventListener('scroll', onScroll, { passive: true, signal });
  }
}

if (!customElements.get('shx-dynamic-grid')) customElements.define('shx-dynamic-grid', ShxDynamicGrid);
